import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Space, Tag, Popconfirm, message, Modal, Form, Upload } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, CopyOutlined, UploadOutlined, DownloadOutlined, DeleteRowOutlined } from '@ant-design/icons';
import useCompanyNavigate from '../../utils/useCompanyNavigate';
import { batchApi } from '../../services/api';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

// 批量新增批次：Excel 列映射
const BATCH_COLUMN_MAP = {
  '批次编号': 'batchNo',
  '批次名称': 'name',
  '品类': 'category',
  '料种': 'jadeTypeName',
  '原石产地': 'rawMaterialOrigin',
  '原石日期': 'rawMaterialDate',
  '入库日期': 'rawMaterialEntryDate',
  '描述': 'description',
};

const BATCH_TEMPLATE_HEADERS = Object.keys(BATCH_COLUMN_MAP);

// 统一日期为 YYYY-MM-DD
const normalizeDate = (val) => {
  if (!val) return '';
  let m = val.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  m = val.match(/(\d{4})[/.](\d{1,2})[/.](\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  m = val.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  return val;
};

// 批量新增批次弹窗
const BatchImportModal = ({ open, onCancel, onSuccess }) => {
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false, dateNF: 'yyyy-mm-dd' });
        if (jsonData.length === 0) {
          message.error('Excel 文件为空');
          return;
        }
        const batchNoSet = new Set();
        const mapped = jsonData.map((row, idx) => {
          const item = { _key: idx, _errors: [] };
          for (const [cnName, enName] of Object.entries(BATCH_COLUMN_MAP)) {
            let val = row[cnName] !== undefined ? String(row[cnName]).trim() : '';
            if ((enName === 'rawMaterialDate' || enName === 'rawMaterialEntryDate') && val) {
              val = normalizeDate(val);
            }
            item[enName] = val;
          }
          return item;
        });
        mapped.forEach(item => {
          if (!item.batchNo) item._errors.push('批次编号为空');
          else if (batchNoSet.has(item.batchNo)) item._errors.push('批次编号重复');
          else batchNoSet.add(item.batchNo);
          if (!item.name) item._errors.push('名称为空');
          ['rawMaterialDate', 'rawMaterialEntryDate'].forEach(f => {
            if (item[f] && !/^\d{4}-\d{2}-\d{2}$/.test(item[f])) item._errors.push('日期格式应为YYYY-MM-DD');
          });
        });
        setDataSource(mapped);
        const errorCount = mapped.filter(i => i._errors.length > 0).length;
        if (errorCount > 0) {
          message.warning(`${errorCount} 条数据存在问题，请检查标红行`);
        } else {
          message.success(`解析成功，共 ${mapped.length} 条数据`);
        }
      } catch (err) {
        message.error('解析 Excel 失败: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([BATCH_TEMPLATE_HEADERS]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '批量新增批次模板');
    XLSX.writeFile(wb, '批量新增批次模板.xlsx');
  };

  const handleCellChange = (key, field, value) => {
    setDataSource(prev => prev.map(item => {
      if (item._key === key) {
        const updated = { ...item, [field]: value };
        updated._errors = [];
        if (!updated.batchNo) updated._errors.push('批次编号为空');
        if (!updated.name) updated._errors.push('名称为空');
        return updated;
      }
      return item;
    }));
  };

  const handleSubmit = async () => {
    const hasErrors = dataSource.some(i => i._errors.length > 0);
    if (hasErrors) {
      message.error('存在错误数据，请修正后再提交');
      return;
    }
    setLoading(true);
    try {
      const batches = dataSource.map(item => ({
        batchNo: item.batchNo,
        name: item.name,
        category: item.category || undefined,
        jadeTypeName: item.jadeTypeName || undefined,
        rawMaterialOrigin: item.rawMaterialOrigin || undefined,
        rawMaterialDate: item.rawMaterialDate || undefined,
        rawMaterialEntryDate: item.rawMaterialEntryDate || undefined,
        description: item.description || undefined,
      }));
      const res = await batchApi.batchImport({ batches });
      message.success(res.message || '批量新增成功');
      setDataSource([]);
      onSuccess();
    } catch (err) {
      message.error(err.message || '批量新增失败');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDataSource([]);
    onCancel();
  };

  const errorCount = dataSource.filter(i => i._errors.length > 0).length;

  const columns = [
    {
      title: '状态', dataIndex: '_errors', width: 60, render: (errors) =>
        errors.length > 0 ? <Tag color="red">{errors.length}错</Tag> : <Tag color="green">OK</Tag>
    },
    {
      title: '批次编号', dataIndex: 'batchNo', width: 120, render: (v, r) =>
        <Input value={v} size="small" status={r._errors.some(e => e.includes('批次编号')) ? 'error' : undefined}
          onChange={e => handleCellChange(r._key, 'batchNo', e.target.value)} />
    },
    {
      title: '批次名称', dataIndex: 'name', width: 120, render: (v, r) =>
        <Input value={v} size="small" status={r._errors.includes('名称为空') ? 'error' : undefined}
          onChange={e => handleCellChange(r._key, 'name', e.target.value)} />
    },
    { title: '品类', dataIndex: 'category', width: 90, render: (v, r) => <Input value={v} size="small" onChange={e => handleCellChange(r._key, 'category', e.target.value)} /> },
    { title: '料种', dataIndex: 'jadeTypeName', width: 90, render: (v, r) => <Input value={v} size="small" onChange={e => handleCellChange(r._key, 'jadeTypeName', e.target.value)} /> },
    { title: '原石产地', dataIndex: 'rawMaterialOrigin', width: 100, render: (v, r) => <Input value={v} size="small" onChange={e => handleCellChange(r._key, 'rawMaterialOrigin', e.target.value)} /> },
    { title: '原石日期', dataIndex: 'rawMaterialDate', width: 110, render: (v, r) => <Input value={v} size="small" placeholder="YYYY-MM-DD" onChange={e => handleCellChange(r._key, 'rawMaterialDate', e.target.value)} /> },
    { title: '入库日期', dataIndex: 'rawMaterialEntryDate', width: 110, render: (v, r) => <Input value={v} size="small" placeholder="YYYY-MM-DD" onChange={e => handleCellChange(r._key, 'rawMaterialEntryDate', e.target.value)} /> },
    { title: '描述', dataIndex: 'description', width: 120, render: (v, r) => <Input value={v} size="small" onChange={e => handleCellChange(r._key, 'description', e.target.value)} /> },
    {
      title: '操作', width: 60, render: (_, r) =>
        <Popconfirm title="删除此行?" onConfirm={() => setDataSource(prev => prev.filter(i => i._key !== r._key))}>
          <Button type="text" danger size="small" icon={<DeleteRowOutlined />} />
        </Popconfirm>
    },
  ];

  return (
    <Modal
      title="批量新增批次"
      open={open}
      onCancel={handleClose}
      footer={null}
      width={1000}
    >
      <div style={{ marginBottom: 12 }}>
        <p style={{ color: '#999', fontSize: 13 }}>
          Excel 列名：批次编号、批次名称、品类、料种、原石产地、原石日期、入库日期、描述（批次编号和名称必填，品类/料种需与系统中已有的一致）
        </p>
        <Space style={{ marginBottom: 8 }}>
          <Upload accept=".xlsx,.xls,.csv" showUploadList={false} beforeUpload={handleFileUpload}>
            <Button icon={<UploadOutlined />}>选择 Excel 文件</Button>
          </Upload>
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>下载模板</Button>
        </Space>
      </div>

      {dataSource.length > 0 && (
        <>
          <div style={{ marginBottom: 8 }}>
            共 <strong>{dataSource.length}</strong> 条
            {errorCount > 0 && <span style={{ marginLeft: 16, color: '#ff4d4f' }}>{errorCount} 条有误</span>}
          </div>
          <Table
            dataSource={dataSource}
            columns={columns}
            rowKey="_key"
            size="small"
            scroll={{ x: 1000 }}
            pagination={dataSource.length > 20 ? { pageSize: 20 } : false}
            rowClassName={(record) => record._errors.length > 0 ? 'batch-row-error' : ''}
          />
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleClose}>取消</Button>
              <Button type="primary" style={{ background: '#8B7355' }} loading={loading}
                disabled={errorCount > 0}
                onClick={handleSubmit}
              >
                确认新增 ({dataSource.length} 条)
              </Button>
            </Space>
          </div>
          <style>{`.batch-row-error { background: #fff2f0 !important; } .batch-row-error:hover > td { background: #fff2f0 !important; }`}</style>
        </>
      )}
    </Modal>
  );
};

const Batches = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [keyword, setKeyword] = useState('');
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyingBatch, setCopyingBatch] = useState(null);
  const [copyLoading, setCopyLoading] = useState(false);
  const [batchImportOpen, setBatchImportOpen] = useState(false);
  const [copyForm] = Form.useForm();
  const navigate = useCompanyNavigate();

  useEffect(() => {
    fetchData(1);
  }, []);

  const fetchData = async (page = 1, searchKeyword = keyword) => {
    setLoading(true);
    try {
      const res = await batchApi.list({
        page,
        limit: pagination.pageSize,
        keyword: searchKeyword
      });
      setData(res.data.list);
      setPagination({
        ...pagination,
        current: page,
        total: res.data.pagination.total
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchData(1, keyword);
  };

  const handleDelete = async (id) => {
    try {
      await batchApi.delete(id);
      message.success('删除成功');
      fetchData(pagination.current);
    } catch {
      // 错误已在拦截器处理
    }
  };

  const handleCopyClick = (record) => {
    setCopyingBatch(record);
    copyForm.setFieldsValue({ batchNo: record.batch_no, name: record.name });
    setCopyModalOpen(true);
  };

  const handleCopySubmit = async () => {
    try {
      const values = await copyForm.validateFields();
      setCopyLoading(true);
      const res = await batchApi.copy(copyingBatch.id, values);
      message.success(res.message || '复制成功');
      setCopyModalOpen(false);
      fetchData(pagination.current);
    } catch (err) {
      if (err.response?.data?.message) {
        message.error(err.response.data.message);
      }
    } finally {
      setCopyLoading(false);
    }
  };

  const getStatusTag = (status) => {
    const map = {
      0: { color: 'default', text: '未开始' },
      1: { color: 'processing', text: '生产中' },
      2: { color: 'success', text: '已完成' }
    };
    const s = map[status] || map[0];
    return <Tag color={s.color}>{s.text}</Tag>;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '批次编号',
      dataIndex: 'batch_no',
      key: 'batch_no',
      render: (text) => <code style={{ color: '#8B7355' }}>{text}</code>
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: '品类',
      dataIndex: 'category',
      key: 'category',
      render: (cat) => cat ? <Tag color="#8B7355">{cat}</Tag> : '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: getStatusTag
    },
    {
      title: '货品数',
      dataIndex: 'product_count',
      key: 'product_count',
      render: (count) => count > 0 ? <Tag color="blue">{count} 件</Tag> : '0'
    },
    {
      title: '照片数',
      dataIndex: 'photo_count',
      key: 'photo_count',
      render: (count) => count > 0 ? <Tag color="green">{count} 张</Tag> : '0'
    },
    {
      title: '生产开始',
      dataIndex: 'production_start_date',
      key: 'production_start_date',
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : '-'
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => navigate(`/admin/batches/${record.id}/edit`)}
          >
            编辑
          </Button>
          <Button
            type="text"
            icon={<CopyOutlined />}
            onClick={() => handleCopyClick(record)}
          >
            复制
          </Button>
          <Popconfirm
            title="确定删除此批次吗？"
            description="批次下的货品必须先删除"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <h2>批次管理</h2>
        <Space>
          <Button
            icon={<UploadOutlined />}
            onClick={() => setBatchImportOpen(true)}
          >
            批量新增批次
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/admin/batches/new')}
            style={{ background: '#8B7355' }}
          >
            新增批次
          </Button>
        </Space>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="搜索批次编号或名称"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onSearch={handleSearch}
          onPressEnter={handleSearch}
          style={{ width: 300 }}
          prefix={<SearchOutlined />}
          allowClear
        />
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: false,
          showTotal: (total) => `共 ${total} 条`
        }}
        onChange={(p) => fetchData(p.current)}
      />

      {/* 复制批次弹框 */}
      <Modal
        title="复制批次"
        open={copyModalOpen}
        onOk={handleCopySubmit}
        onCancel={() => setCopyModalOpen(false)}
        confirmLoading={copyLoading}
        okText="确定复制"
        cancelText="取消"
      >
        <Form form={copyForm} layout="vertical">
          <Form.Item name="batchNo" label="批次编号" rules={[{ required: true, message: '请输入批次编号' }]}>
            <Input placeholder="请输入新的批次编号" />
          </Form.Item>
          <Form.Item name="name" label="批次名称" rules={[{ required: true, message: '请输入批次名称' }]}>
            <Input placeholder="请输入新的批次名称" />
          </Form.Item>
        </Form>
        <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
          将复制原石照片和切料照片到新批次
        </div>
      </Modal>

      {/* 批量新增批次弹框 */}
      <BatchImportModal
        open={batchImportOpen}
        onCancel={() => setBatchImportOpen(false)}
        onSuccess={() => {
          setBatchImportOpen(false);
          fetchData(1);
        }}
      />
    </div>
  );
};

export default Batches;
